<template>
  <v-row>
    <v-col cols="12">
      <!-- Aviso legal -->
      <v-alert type="warning" outlined dense class="mb-4" icon="mdi-alert-circle-outline">
        {{ legalNotice }}
      </v-alert>

      <!-- Info sobre comodatos firmados -->
      <v-alert type="info" text dense class="mb-4" icon="mdi-information-outline">
        Los cambios en el clausulado solo afectan a comodatos aún sin firmar y a los nuevos. Los comodatos ya firmados conservan el clausulado vigente en el momento de la firma.
      </v-alert>
    </v-col>

    <!-- Panel izquierdo: lista de cláusulas -->
    <v-col cols="12" :md="showPreview ? 6 : 12">
      <v-card class="elevation-2 rounded-lg">
        <v-card-title class="py-3 primary--text">
          <v-icon left color="primary">mdi-format-list-numbered</v-icon>
          Clausulado del Comodato
          <v-chip v-if="templateInfo && templateInfo.name" x-small outlined color="primary" class="ml-3" title="Plantilla sobre la que trabajas">
            <v-icon x-small left>mdi-file-document-outline</v-icon>
            {{ templateInfo.name }}
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
            v-if="!showPreview"
            text small color="primary"
            @click="showPreview = true; loadPreview()"
          >
            <v-icon left small>mdi-eye</v-icon>
            Vista previa
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text v-if="loading" class="pa-6 text-center">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
          <div class="mt-2 grey--text">Cargando clausulado...</div>
        </v-card-text>

        <v-card-text v-else-if="!clauses.length" class="pa-6 text-center">
          <v-icon size="48" color="grey lighten-1">mdi-text-box-remove-outline</v-icon>
          <div class="mt-2 grey--text">No hay cláusulas configuradas</div>
        </v-card-text>

        <v-card-text v-else class="pa-2">
          <draggable
            v-model="clauses"
            handle=".drag-handle"
            :disabled="!canEdit"
            :move="onMove"
            @end="onDragEnd"
            ghost-class="clause-ghost"
            animation="200"
          >
            <transition-group type="transition" name="clause-list">
              <v-card
                v-for="(clause, index) in clauses"
                :key="clause.key"
                outlined
                class="mb-2 clause-card"
                :class="{
                  'clause-fixed': clause.type === 'fija',
                  'clause-disabled': clause.type === 'opcional' && !clause.enabled,
                  'clause-editing': editingClauseKey === clause.key
                }"
              >
                <div class="d-flex align-start pa-3">
                  <!-- Drag handle -->
                  <div
                    v-if="canEdit && clause.type !== 'fija'"
                    class="drag-handle mr-2 mt-1"
                    style="cursor: grab;"
                  >
                    <v-icon small color="grey">mdi-drag-vertical</v-icon>
                  </div>
                  <div v-else class="mr-2 mt-1" style="width: 24px;"></div>

                  <!-- Número de orden -->
                  <v-avatar size="28" color="grey lighten-3" class="mr-3 mt-1 flex-shrink-0">
                    <span class="caption font-weight-medium">{{ index + 1 }}</span>
                  </v-avatar>

                  <!-- Contenido -->
                  <div class="flex-grow-1" style="min-width: 0;">
                    <!-- Header de la cláusula -->
                    <div class="d-flex align-center flex-wrap mb-1">
                      <span class="subtitle-2 font-weight-medium mr-2">{{ clause.title }}</span>

                      <!-- Badge de tipo -->
                      <v-chip x-small class="mr-1" :color="getTypeColor(clause.type)" :text-color="getTypeTextColor(clause.type)">
                        <v-icon x-small left v-if="clause.type === 'fija'">mdi-lock</v-icon>
                        {{ getTypeLabel(clause.type) }}
                      </v-chip>

                      <!-- Badge modificada -->
                      <v-chip v-if="clause.modified" x-small color="blue" text-color="white" class="mr-1">
                        Modificada
                      </v-chip>
                    </div>

                    <!-- Texto de la cláusula (modo lectura) -->
                    <div
                      v-if="editingClauseKey !== clause.key"
                      class="body-2 grey--text text--darken-2 clause-text"
                    >
                      <span v-html="renderClauseText(clause.text)"></span>
                    </div>

                    <!-- Editor de cláusula -->
                    <div v-if="editingClauseKey === clause.key" class="mt-2">
                      <v-text-field
                        v-if="clause.type === 'personalizada'"
                        v-model="editingTitle"
                        label="Título"
                        outlined dense
                        hide-details="auto"
                        class="mb-2"
                        :rules="[v => !!v || 'El título es obligatorio', v => (v && v.length <= 255) || 'Máximo 255 caracteres']"
                        counter="255"
                      ></v-text-field>

                      <div class="editor-wrapper">
                        <variable-editor
                          ref="clauseEditor"
                          v-model="editingText"
                          label="Texto de la cláusula"
                          :maxlength="5000"
                          required
                        ></variable-editor>

                        <!-- Botón insertar variable -->
                        <v-menu
                          v-model="variableMenu"
                          :close-on-content-click="false"
                          offset-y
                          max-height="400"
                          max-width="350"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              x-small text color="amber darken-3"
                              class="mt-1"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon x-small left>mdi-code-braces</v-icon>
                              Insertar dato
                            </v-btn>
                          </template>
                          <v-card>
                            <v-card-title class="py-2 subtitle-2">
                              Variables automatizadas
                            </v-card-title>
                            <v-divider></v-divider>
                            <v-list dense class="py-0">
                              <template v-for="group in variables">
                                <v-subheader :key="'h-' + group.category" class="font-weight-bold">
                                  {{ group.category }}
                                </v-subheader>
                                <v-list-item
                                  v-for="variable in group.variables"
                                  :key="variable.marker"
                                  @click="insertVariable(variable)"
                                  dense
                                >
                                  <v-list-item-content>
                                    <v-list-item-title class="body-2">{{ variable.name }}</v-list-item-title>
                                    <v-list-item-subtitle class="caption">
                                      Ej: {{ variable.example }}
                                    </v-list-item-subtitle>
                                  </v-list-item-content>
                                  <v-list-item-action>
                                    <v-icon small color="amber darken-2">mdi-plus-circle</v-icon>
                                  </v-list-item-action>
                                </v-list-item>
                              </template>
                            </v-list>
                          </v-card>
                        </v-menu>
                      </div>

                      <!-- Acciones del editor -->
                      <div class="d-flex justify-end mt-2">
                        <v-btn x-small text @click="cancelEditing" class="mr-2">
                          Cancelar
                        </v-btn>
                        <v-btn x-small color="primary" @click="confirmEditing">
                          <v-icon x-small left>mdi-check</v-icon>
                          Aplicar
                        </v-btn>
                      </div>
                    </div>

                    <!-- Acciones de la cláusula -->
                    <div v-if="canEdit && editingClauseKey !== clause.key" class="d-flex align-center mt-1 flex-wrap">
                      <!-- Editable: botón editar + restaurar -->
                      <template v-if="clause.type === 'editable' || clause.type === 'personalizable'">
                        <v-btn x-small text color="primary" @click="startEditing(clause)">
                          <v-icon x-small left>mdi-pencil</v-icon>
                          Editar
                        </v-btn>
                        <v-btn
                          v-if="clause.modified"
                          x-small text color="orange"
                          @click="restoreClause(clause)"
                          :loading="restoringKey === clause.key"
                        >
                          <v-icon x-small left>mdi-restore</v-icon>
                          Restaurar texto original
                        </v-btn>
                      </template>

                      <!-- Opcional: switch -->
                      <template v-if="clause.type === 'opcional'">
                        <v-switch
                          v-model="clause.enabled"
                          dense hide-details
                          class="mt-0 pt-0"
                          :label="clause.enabled ? 'Activada' : 'Desactivada'"
                          @change="markDirty"
                        ></v-switch>
                      </template>

                      <!-- Personalizada: editar + eliminar -->
                      <template v-if="clause.type === 'personalizada'">
                        <v-btn x-small text color="primary" @click="startEditing(clause)">
                          <v-icon x-small left>mdi-pencil</v-icon>
                          Editar
                        </v-btn>
                        <v-btn x-small text color="error" @click="deleteCustomClause(clause)">
                          <v-icon x-small left>mdi-delete</v-icon>
                          Eliminar
                        </v-btn>
                      </template>
                    </div>
                  </div>
                </div>
              </v-card>
            </transition-group>
          </draggable>

          <!-- Botón añadir cláusula personalizada -->
          <v-btn
            v-if="canEdit && customClauseCount < 20"
            text block color="primary"
            class="mt-2"
            @click="addCustomClause"
          >
            <v-icon left>mdi-plus</v-icon>
            Añadir cláusula personalizada
          </v-btn>
          <div v-if="customClauseCount >= 20" class="caption grey--text text-center mt-2">
            Se ha alcanzado el límite de 20 cláusulas personalizadas.
          </div>

          <comodato-signatures-block :has-stamp="hasStamp"></comodato-signatures-block>
        </v-card-text>

        <v-divider v-if="canEdit"></v-divider>

        <v-card-actions v-if="canEdit" class="pa-4">
          <v-chip v-if="isDirty" x-small color="orange" text-color="white">
            <v-icon x-small left>mdi-alert</v-icon>
            Cambios sin guardar
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="saving || !isDirty"
            @click="saveClauses"
          >
            <v-icon left>mdi-content-save</v-icon>
            Guardar clausulado
          </v-btn>
        </v-card-actions>

        <!-- Solo lectura -->
        <v-card-text v-if="!canEdit && !loading && clauses.length" class="pt-0">
          <v-alert type="info" text dense icon="mdi-lock">
            Modo solo lectura. No tienes permiso para editar el clausulado.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Panel derecho: vista previa -->
    <v-col v-if="showPreview" cols="12" md="6">
      <v-card class="elevation-2 rounded-lg preview-card" style="position: sticky; top: 80px;">
        <v-card-title class="py-3 primary--text">
          <v-icon left color="primary">mdi-file-document-outline</v-icon>
          Vista previa
          <v-spacer></v-spacer>
          <v-btn icon small @click="loadPreview" :loading="loadingPreview">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon small @click="showPreview = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text v-if="loadingPreview" class="pa-6 text-center">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
          <div class="mt-2 caption grey--text">Generando vista previa...</div>
        </v-card-text>

        <v-card-text v-else-if="preview" class="pa-4 preview-content">
          <h3 v-if="preview.title" class="text-h6 font-weight-bold text-center mb-2">
            {{ preview.title }}
          </h3>
          <h4 v-if="preview.subtitle" class="subtitle-1 text-center grey--text mb-4">
            {{ preview.subtitle }}
          </h4>
          <div v-html="preview.html" class="preview-html"></div>
          <comodato-signatures-block :has-stamp="hasStamp"></comodato-signatures-block>
        </v-card-text>

        <v-card-text v-else class="pa-6 text-center">
          <v-icon size="48" color="grey lighten-1">mdi-file-document-outline</v-icon>
          <div class="mt-2 grey--text">Guarda los cambios para actualizar la vista previa</div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import draggable from 'vuedraggable'
import VariableEditor from '@/components/VariableEditor'
import ComodatoSignaturesBlock from '@/components/ComodatoSignaturesBlock'
import ComodatoClauseService from '@/services/ComodatoClauseService'

export default {
  components: { draggable, VariableEditor, ComodatoSignaturesBlock },

  props: {
    dealerId: {
      type: [Number, String],
      required: true
    },
    // Si el dealer tiene sello cargado en Comodatos (null = desconocido, no se avisa)
    hasStamp: {
      type: Boolean,
      default: null
    }
  },

  data: () => ({
    loading: false,
    saving: false,
    loadingPreview: false,
    showPreview: false,
    restoringKey: null,

    clauses: [],
    variables: [],
    canEdit: false,
    legalNotice: '',
    templateInfo: null,
    preview: null,
    isDirty: false,

    editingClauseKey: null,
    editingText: '',
    editingTitle: '',
    variableMenu: false,

    nextCustomId: 1
  }),

  computed: {
    customClauseCount () {
      return this.clauses.filter(c => c.type === 'personalizada').length
    }
  },

  watch: {
    dealerId: {
      handler (val) {
        if (val) {
          this.loadClauses()
          this.loadVariables()
        }
      },
      immediate: true
    }
  },

  methods: {
    async loadClauses () {
      if (!this.dealerId) return
      this.loading = true
      try {
        const response = await ComodatoClauseService.getClauses(this.dealerId)
        const data = response.data.data || response.data
        this.templateInfo = data.template
        this.clauses = data.clauses || []
        this.canEdit = data.can_edit || false
        this.legalNotice = data.legal_notice || 'El contenido de las cláusulas editables y personalizadas es responsabilidad de la concesión.'
        this.isDirty = false
      } catch (error) {
        console.error('Error al cargar clausulado:', error)
        this.$toast.error('Error al cargar el clausulado del comodato')
      } finally {
        this.loading = false
      }
    },

    async loadVariables () {
      try {
        const response = await ComodatoClauseService.getVariables()
        this.variables = response.data.data || response.data || []
      } catch (error) {
        console.error('Error al cargar variables:', error)
      }
    },

    async loadPreview () {
      if (!this.dealerId) return
      this.loadingPreview = true
      try {
        const response = await ComodatoClauseService.getPreview(this.dealerId)
        this.preview = response.data.data || response.data
      } catch (error) {
        console.error('Error al cargar vista previa:', error)
        this.$toast.error('Error al cargar la vista previa')
      } finally {
        this.loadingPreview = false
      }
    },

    async saveClauses () {
      if (!this.canEdit || !this.isDirty) return
      this.saving = true
      try {
        const payload = {
          clauses: this.clauses.map(clause => {
            if (clause.type === 'fija') {
              return { id: clause.id }
            }
            if (clause.type === 'editable' || clause.type === 'personalizable') {
              return { id: clause.id, text: clause.text }
            }
            if (clause.type === 'opcional') {
              return { id: clause.id, enabled: clause.enabled }
            }
            if (clause.type === 'personalizada') {
              const item = { title: clause.title, text: clause.text }
              if (clause.dealer_clause_id) {
                item.dealer_clause_id = clause.dealer_clause_id
              }
              return item
            }
            return { id: clause.id }
          })
        }

        const response = await ComodatoClauseService.saveClauses(this.dealerId, payload)
        const data = response.data.data || response.data
        this.clauses = data.clauses || this.clauses
        this.canEdit = data.can_edit !== undefined ? data.can_edit : this.canEdit
        this.isDirty = false
        this.$toast.success('Clausulado guardado correctamente')

        if (this.showPreview) {
          this.loadPreview()
        }
      } catch (error) {
        console.error('Error al guardar clausulado:', error)
        if (error.response && error.response.status === 422) {
          const msg = error.response.data?.data?.message || error.response.data?.message || 'Error de validación'
          this.$toast.error(msg)
        } else if (error.response && error.response.status === 403) {
          this.canEdit = false
          this.$toast.error('No tienes permiso para editar el clausulado (editar_comodato)')
        } else {
          this.$toast.error('Error al guardar el clausulado')
        }
      } finally {
        this.saving = false
      }
    },

    async restoreClause (clause) {
      if (this.isDirty) {
        const ok = await this.$confirm(
          'Hay cambios sin guardar. Al restaurar el texto original se recarga el clausulado y se perderán. ¿Continuar?',
          { title: 'Cambios sin guardar', buttonTrueText: 'Sí, restaurar', buttonFalseText: 'Cancelar' }
        )
        if (!ok) return
      }
      this.restoringKey = clause.key
      try {
        const response = await ComodatoClauseService.restoreClause(this.dealerId, clause.id)
        const data = response.data.data || response.data
        this.clauses = data.clauses || this.clauses
        this.isDirty = false
        this.$toast.success('Cláusula restaurada al texto original')

        if (this.showPreview) {
          this.loadPreview()
        }
      } catch (error) {
        console.error('Error al restaurar cláusula:', error)
        if (error.response && error.response.status === 422) {
          const msg = error.response.data?.data?.message || 'No se puede restaurar esta cláusula'
          this.$toast.error(msg)
        } else {
          this.$toast.error('Error al restaurar la cláusula')
        }
      } finally {
        this.restoringKey = null
      }
    },

    startEditing (clause) {
      this.editingClauseKey = clause.key
      this.editingText = clause.text || ''
      this.editingTitle = clause.title || ''
    },

    cancelEditing () {
      this.editingClauseKey = null
      this.editingText = ''
      this.editingTitle = ''
      this.variableMenu = false
    },

    confirmEditing () {
      const clause = this.clauses.find(c => c.key === this.editingClauseKey)
      if (!clause) return

      if (clause.type === 'personalizada' && !this.editingTitle.trim()) {
        this.$toast.error('El título es obligatorio')
        return
      }
      if (!this.editingText.trim()) {
        this.$toast.error('El texto es obligatorio')
        return
      }

      if (this.editingText.length > 5000) {
        this.$toast.error('El texto admite 5000 caracteres como máximo')
        return
      }

      clause.text = this.editingText
      if (clause.type === 'personalizada') {
        clause.title = this.editingTitle
      }
      if (clause.type === 'editable' || clause.type === 'personalizable') {
        clause.modified = clause.text !== clause.default_text
      }

      this.markDirty()
      this.cancelEditing()
    },

    // Inserta la variable como chip en la posición del cursor
    insertVariable (variable) {
      const editor = Array.isArray(this.$refs.clauseEditor) ? this.$refs.clauseEditor[0] : this.$refs.clauseEditor
      if (editor) {
        editor.insertVariable(variable.marker)
      } else {
        this.editingText += variable.marker
      }
      this.variableMenu = false
    },

    addCustomClause () {
      if (this.customClauseCount >= 20) return
      const key = 'new-' + this.nextCustomId++
      this.clauses.push({
        key,
        id: null,
        dealer_clause_id: null,
        code: null,
        type: 'personalizada',
        title: '',
        text: '',
        default_text: null,
        modified: false,
        enabled: true,
        locked: false,
        order: this.clauses.length
      })
      this.startEditing(this.clauses[this.clauses.length - 1])
      this.markDirty()
    },

    deleteCustomClause (clause) {
      const index = this.clauses.findIndex(c => c.key === clause.key)
      if (index > -1) {
        this.clauses.splice(index, 1)
        this.markDirty()
      }
    },

    // Las fijas van siempre primero: no se pueden soltar por encima de ellas
    onMove (evt) {
      return !evt.related.classList.contains('clause-fixed')
    },

    onDragEnd () {
      this.markDirty()
    },

    markDirty () {
      this.isDirty = true
    },

    renderClauseText (text) {
      if (!text) return ''
      let escaped = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
      escaped = escaped.replace(/\n/g, '<br>')
      escaped = escaped.replace(
        /\{\{([^}]+)\}\}/g,
        '<span class="variable-chip">$1</span>'
      )
      return escaped
    },

    getTypeColor (type) {
      const colors = {
        fija: 'grey lighten-2',
        editable: 'blue lighten-4',
        personalizable: 'purple lighten-4',
        opcional: 'green lighten-4',
        personalizada: 'amber lighten-4'
      }
      return colors[type] || 'grey lighten-2'
    },

    getTypeTextColor (type) {
      const colors = {
        fija: 'grey darken-2',
        editable: 'blue darken-3',
        personalizable: 'purple darken-3',
        opcional: 'green darken-3',
        personalizada: 'amber darken-4'
      }
      return colors[type] || 'grey darken-2'
    },

    getTypeLabel (type) {
      const labels = {
        fija: 'Fija',
        editable: 'Editable',
        personalizable: 'Personalizable (Grupo)',
        opcional: 'Opcional',
        personalizada: 'Personalizada'
      }
      return labels[type] || type
    }
  }
}
</script>

<style scoped>
.clause-card {
  transition: all 0.2s ease;
  border-left: 3px solid transparent;
}

.clause-card:hover {
  border-left-color: var(--v-primary-base, #1976d2);
}

.clause-disabled {
  opacity: 0.55;
}

.clause-editing {
  border-left-color: var(--v-primary-base, #1976d2) !important;
  background-color: #f5f9ff;
}

.clause-ghost {
  opacity: 0.4;
  background: #e3f2fd;
}

.clause-text {
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 120px;
  overflow: hidden;
  position: relative;
}

.clause-list-move {
  transition: transform 0.3s;
}

.drag-handle:hover .v-icon {
  color: var(--v-primary-base, #1976d2) !important;
}

.variable-chip {
  display: inline;
  background-color: #fff3e0;
  color: #e65100;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 0.85em;
  border: 1px solid #ffe0b2;
  white-space: nowrap;
}

.preview-content {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.preview-html >>> ol {
  padding-left: 20px;
}

.preview-html >>> li {
  margin-bottom: 12px;
  line-height: 1.6;
}

.preview-html >>> strong {
  color: #333;
}

.editor-wrapper {
  position: relative;
}
</style>
